import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Offcanvas } from '@core/components/ui/Offcanvas';
import { Spinner } from '@core/components/ui/Spinner';
import { Badge } from '@core/components/ui/Badge';
import { formatDateCompact } from '@core/components/ui/utils/formatters';
import {
  useGetConversationDetailQuery,
  useSendAdminMessageMutation,
} from '../api/conversationsApi';
import { getSourceBadge } from '../utils/conversationUtils';
import { MessageList } from './MessageList';

export function ConversationOffcanvas({ isOpen, sessionId, onClose }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetConversationDetailQuery(sessionId, {
    skip: !isOpen,
  });
  const [sendAdminMessage, { isLoading: isSending }] = useSendAdminMessageMutation();

  const [messageText, setMessageText] = useState('');

  const source = data?.session?.source;
  const isWhatsApp = source === 'whatsapp';
  const isWebApp = source === 'webapp';
  const isWithin24h = data?.session?.is_within_24h_window;
  // Message input visible for whatsapp and webapp sources
  const showMessageInput = isWhatsApp || isWebApp;
  // For webapp: no 24h window restriction. For WhatsApp: respect the 24h window.
  const canSendMessage = isWebApp || isWithin24h;

  const handleClose = () => {
    onClose();
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    await sendAdminMessage({ sessionId, message: messageText.trim() }).unwrap();
    setMessageText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const sourceBadge = data ? getSourceBadge(data.session.source, t) : null;

  const title = data ? (
    <div className="flex flex-col gap-1.5">
      {/* Row 1: Name */}
      <div className="font-semibold text-default">
        {data.user.first_name} {data.user.last_name}
      </div>
      {/* Row 2: Email + Phone */}
      <div className="text-xs text-muted font-normal flex items-center gap-2">
        <span>{data.user.email}</span>
        {data.user.phone && (
          <>
            <span className="text-subtle">·</span>
            <span>{data.user.phone}</span>
          </>
        )}
      </div>
      {/* Row 3: Badges + last message + 24h window */}
      <div className="flex items-center gap-2 flex-wrap mt-0.5">
        <Badge variant={sourceBadge.variant}>{sourceBadge.label}</Badge>
        {isWhatsApp && (
          isWithin24h
            ? <Badge variant="success">{t('conversations.windowActive')}</Badge>
            : <Badge variant="status-inactive">{t('conversations.windowExpiredLabel')}</Badge>
        )}
        <span className="text-xs text-muted whitespace-nowrap">
          {t('conversations.lastActive')}: {formatDateCompact(data.session.last_active)}
        </span>
      </div>
    </div>
  ) : null;

  return (
    <Offcanvas
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      side="right"
      size="xl"
    >
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="p-4 text-error">
          {error.error || t('superadmin.error')}
        </div>
      ) : data ? (
        <div className="flex flex-col h-full">
          {/* Message List */}
          <div className="flex-1 overflow-y-auto p-4">
            <MessageList messages={data.messages} sessionId={sessionId} />
          </div>

          {/* Footer: Message input (always visible for supported sources) */}
          {showMessageInput && (
            <div className="border-t border-border-default p-4">
              {canSendMessage ? (
                <div className="relative">
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('conversations.messagePlaceholder')}
                    rows={2}
                    className="p-3 pb-10 block w-full border border-border-default rounded-xl text-sm placeholder:text-subtle/80 focus:outline-none focus:border-border-default focus:ring-0 focus:shadow-[0_0_0_3px_rgba(0,0,0,0.05)] resize-none overflow-y-auto max-h-[8rem]"
                  />
                  <div className="absolute bottom-px inset-x-px p-1.5 rounded-b-xl bg-surface">
                    <div className="flex justify-end items-center">
                      <button
                        type="button"
                        onClick={handleSendMessage}
                        disabled={isSending || !messageText.trim()}
                        aria-label={t('conversations.sendMessage')}
                        className="inline-flex shrink-0 justify-center items-center w-8 h-8 rounded-full text-white bg-primary hover:bg-primary-dark focus:outline-none focus:bg-primary-dark disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
                      >
                        <Icon icon="lucide:arrow-up" className="shrink-0 size-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted">
                  <Icon icon="lucide:clock" className="size-4 text-warning" />
                  {t('conversations.windowExpired')}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </Offcanvas>
  );
}

ConversationOffcanvas.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  sessionId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
